import type { Vacancy } from '../../types';
import type { User } from '../../types';
import type { BulkCandidate } from '../BulkWhatsAppComposerModal';
import type {
  AssignmentEndingRow,
  DataConsentRow,
  EngagementTask,
  MockCandidate,
  PlacementFollowupRow,
  WarmthStatus,
} from './types';

function unwrap(row: any): any {
  return row?.candidate || row;
}

function daysSince(iso?: string | null): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return Math.floor((Date.now() - t) / 86400000);
}

function daysUntil(iso?: string | null): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return Math.floor((t - Date.now()) / 86400000);
}

function tokenOverlap(a: string, b: string): number {
  const tok = (s: string) =>
    new Set(
      (s || '')
        .toLowerCase()
        .split(/[^a-z0-9à-öø-ÿ]+/i)
        .filter((w) => w.length > 2)
    );
  const A = tok(a);
  const B = tok(b);
  let n = 0;
  for (const w of A) {
    if (B.has(w)) n++;
  }
  return n;
}

function matchScore(candidateRole: string, vacancyTitle: string): number {
  const overlap = tokenOverlap(candidateRole || '', vacancyTitle || '');
  return Math.min(98, 42 + overlap * 12);
}

function formatNlDate(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  return d.toLocaleDateString('nl-NL');
}

function pickCompany(v: Vacancy): string {
  return (
    v.client?.name ||
    v.eindklant?.name ||
    (v.data as any)?.companyName ||
    '—'
  );
}

function consentFlags(c: any): { consent_ok: boolean; opted_out: boolean } {
  const tags = (c?.tags as string[]) || [];
  const ai =
    c?.ai_parsed_data && typeof c.ai_parsed_data === 'object' && !Array.isArray(c.ai_parsed_data)
      ? (c.ai_parsed_data as Record<string, unknown>)
      : {};
  const raw = c?.data && typeof c.data === 'object' && !Array.isArray(c.data) ? (c.data as Record<string, unknown>) : {};
  const data = { ...ai, ...raw };
  const opted =
    tags.some((t) => /niet\s*benaderen|do not contact|opt-?out/i.test(t)) ||
    data?.do_not_contact === true ||
    data?.opt_out === true;
  const consentMissing = data?.gdpr_consent === false || data?.consent_ok === false;
  return { consent_ok: !consentMissing && !opted, opted_out: opted };
}

function baseMessage(userName: string, candName: string, reason: string): string {
  return `Hoi ${candName.split(/\s+/)[0] || candName},\n\n${reason}\n\nGroet,\n${userName}`;
}

/** Derive engagement queue from talent pool rows + placements + vacancies (heuristic). */
export function buildLiveTodayTasks(
  poolRows: any[],
  placements: any[],
  vacancies: Vacancy[],
  user: User
): EngagementTask[] {
  const nowIso = new Date().toISOString();
  const recruiterName = user.name || 'Recruiter';
  const tasks: EngagementTask[] = [];
  const seen = new Set<string>();

  const candidates = poolRows.map((r) => unwrap(r)).filter(Boolean);

  for (const p of placements || []) {
    const cand = p.candidate;
    const vac = p.vacancy;
    if (!cand?.id) continue;
    const st = (p.status || '').toLowerCase();
    const active =
      st.includes('actief') ||
      st.includes('active') ||
      st === 'voltooid' ||
      st.includes('geplaatst');
    if (!active) continue;

    const startDays = daysSince(p.start_date);
    const untilEnd = daysUntil(p.end_date);

    if (startDays !== null && startDays >= 14 && startDays <= 120) {
      const id = `plc-follow-${p.id}`;
      if (!seen.has(id)) {
        seen.add(id);
        const company =
          p.opdrachtgever?.name ||
          p.eindklant?.name ||
          vac?.client?.name ||
          'opdrachtgever';
        tasks.push({
          id,
          candidate_id: cand.id,
          candidate_name: cand.name || 'Kandidaat',
          candidate_phone: cand.phone || '',
          candidate_email: cand.email || '',
          vacancy_id: p.vacancy_id || null,
          placement_id: p.id,
          task_type: 'placement_followup',
          reason: `Placement gestart ${formatNlDate(p.start_date)} — check-in bij ${company}`,
          priority: startDays > 45 ? 'medium' : 'high',
          engagement_score: 68 + Math.min(22, Math.floor(startDays / 7)),
          ai_confidence: 0.72,
          suggested_channel: 'email',
          suggested_send_time: 'Komende werkdag ochtend',
          generated_message: baseMessage(
            recruiterName,
            cand.name || '',
            `Ik wil graag even horen hoe je je eerste weken bij ${company} beleeft. Lukt het om kort te reageren?`
          ),
          status: 'ready_to_contact',
          assigned_recruiter_id: user.id,
          assigned_recruiter_name: recruiterName,
          last_contacted_at: formatNlDate(cand.updated_at),
          created_at: nowIso,
          updated_at: nowIso,
          ...consentFlags(cand),
          current_status: cand.status || cand.role || '—',
        });
      }
    }

    if (p.end_date && untilEnd !== null && untilEnd >= 0 && untilEnd <= 30) {
      const id = `plc-end-${p.id}`;
      if (!seen.has(id)) {
        seen.add(id);
        const company = pickCompany(vac || ({} as Vacancy));
        tasks.push({
          id,
          candidate_id: cand.id,
          candidate_name: cand.name || 'Kandidaat',
          candidate_phone: cand.phone || '',
          candidate_email: cand.email || '',
          vacancy_id: p.vacancy_id || null,
          placement_id: p.id,
          task_type: 'assignment_ending',
          reason: `Opdracht eindigt rond ${formatNlDate(p.end_date)} — beschikbaarheid en vervolg afstemmen`,
          priority: untilEnd <= 14 ? 'high' : 'medium',
          engagement_score: 74 + Math.min(15, 30 - Math.max(0, untilEnd)),
          ai_confidence: 0.78,
          suggested_channel: 'phone',
          suggested_send_time: 'Vandaag',
          generated_message: baseMessage(
            recruiterName,
            cand.name || '',
            `Je opdracht bij ${company} loopt naar het einde (${formatNlDate(p.end_date)}). Laten we kort afstemmen wat jouw volgende stap wordt.`
          ),
          status: 'ready_to_contact',
          assigned_recruiter_id: user.id,
          assigned_recruiter_name: recruiterName,
          last_contacted_at: formatNlDate(cand.updated_at),
          created_at: nowIso,
          updated_at: nowIso,
          ...consentFlags(cand),
          current_status: cand.status || '—',
        });
      }
    }
  }

  for (const c of candidates) {
    const stale = daysSince(c.updated_at);
    const { consent_ok, opted_out } = consentFlags(c);
    if (stale !== null && stale >= 60 && consent_ok && !opted_out) {
      const id = `warm-${c.id}`;
      if (!seen.has(id)) {
        seen.add(id);
        tasks.push({
          id,
          candidate_id: c.id,
          candidate_name: c.name || 'Kandidaat',
          candidate_phone: c.phone || '',
          candidate_email: c.email || '',
          vacancy_id: null,
          placement_id: null,
          task_type: 'warm_candidate_followup',
          reason: `Laatste update ${stale} dagen geleden — warme reconnect`,
          priority: stale >= 120 ? 'high' : 'medium',
          engagement_score: Math.min(95, 52 + Math.min(40, Math.floor(stale / 10))),
          ai_confidence: 0.68,
          suggested_channel: 'whatsapp_personal',
          suggested_send_time: 'Woensdag–vrijdag middag',
          generated_message: baseMessage(
            recruiterName,
            c.name || '',
            'Lang niet gesproken — ik heb opnieuw interessante rollen die bij je profiel passen. Heb je even voor een korte app of belafspraak?'
          ),
          status: 'draft_ready',
          assigned_recruiter_id: user.id,
          assigned_recruiter_name: recruiterName,
          last_contacted_at: formatNlDate(c.updated_at),
          created_at: nowIso,
          updated_at: nowIso,
          consent_ok,
          opted_out,
          current_status: c.status || c.role || '—',
        });
      }
    }
  }

  const vacList = (vacancies || []).filter((v) => v?.title);
  for (const c of candidates.slice(0, 120)) {
    const { consent_ok, opted_out } = consentFlags(c);
    if (!consent_ok || opted_out) continue;
    let best: { v: Vacancy; score: number } | null = null;
    for (const v of vacList) {
      const sc = matchScore(c.role || '', v.title);
      if (!best || sc > best.score) best = { v, score: sc };
    }
    if (best && best.score >= 48) {
      const id = `vac-${c.id}-${best.v.id}`;
      if (!seen.has(id)) {
        seen.add(id);
        const company = pickCompany(best.v);
        tasks.push({
          id,
          candidate_id: c.id,
          candidate_name: c.name || 'Kandidaat',
          candidate_phone: c.phone || '',
          candidate_email: c.email || '',
          vacancy_id: best.v.id,
          placement_id: null,
          task_type: 'new_vacancy_outreach',
          reason: `Match ${best.score}% op "${best.v.title}" (${company})`,
          priority: best.score >= 72 ? 'high' : 'medium',
          engagement_score: best.score,
          ai_confidence: Math.min(0.94, 0.55 + best.score / 220),
          suggested_channel: 'whatsapp_personal',
          suggested_send_time: 'Vandaag 14:00–17:00',
          generated_message: baseMessage(
            recruiterName,
            c.name || '',
            `Ik heb de rol "${best.v.title}" bij ${company} die goed lijkt aan te sluiten op jouw ervaring (${c.role || 'profiel'}). Zullen we dit kort bespreken?`
          ),
          status: 'ready_to_contact',
          assigned_recruiter_id: user.id,
          assigned_recruiter_name: recruiterName,
          last_contacted_at: formatNlDate(c.updated_at),
          created_at: nowIso,
          updated_at: nowIso,
          consent_ok,
          opted_out,
          current_status: c.status || '—',
        });
      }
    }
  }

  tasks.sort((a, b) => b.engagement_score - a.engagement_score);
  return tasks.slice(0, 80);
}

export function buildVacancyMatches(
  vacancyId: string | undefined,
  poolRows: any[],
  vacancies: Vacancy[]
): MockCandidate[] {
  const vac = vacancies.find((v) => v.id === vacancyId) || vacancies[0];
  if (!vac) return [];
  const out: MockCandidate[] = [];
  for (const row of poolRows.slice(0, 80)) {
    const c = unwrap(row);
    if (!c?.id) continue;
    const score = matchScore(c.role || '', vac.title);
    if (score < 45) continue;
    const stale = daysSince(c.updated_at) ?? 0;
    let warmth: WarmthStatus = 'warm';
    if (stale > 90) warmth = 'passive';
    else if (stale > 30) warmth = 'warm';
    else warmth = 'hot';
    const { opted_out } = consentFlags(c);
    if (opted_out) warmth = 'do_not_contact';
    out.push({
      id: c.id,
      name: c.name || 'Naamloos',
      email: c.email || '',
      phone: c.phone || '',
      role: c.role || '',
      location: c.address_city || c.address_country || '—',
      warmth,
      last_contact: formatNlDate(c.updated_at),
      engagement_score: score,
      opted_out,
    });
  }
  out.sort((a, b) => b.engagement_score - a.engagement_score);
  return out.slice(0, 40);
}

export function buildPlacementFollowups(placements: any[]): PlacementFollowupRow[] {
  return (placements || [])
    .filter((p) => p?.candidate)
    .slice(0, 60)
    .map((p) => {
      const cand = p.candidate;
      const vac = p.vacancy;
      const client =
        p.opdrachtgever?.name || p.eindklant?.name || vac?.client?.name || vac?.eindklant?.name || '—';
      const startDays = daysSince(p.start_date);
      let satisfaction: PlacementFollowupRow['satisfaction'] = 'unknown';
      let risk = 35;
      if (startDays !== null && startDays > 60) {
        satisfaction = 'neutral';
        risk = 48;
      }
      if (startDays !== null && startDays > 100) {
        satisfaction = 'at_risk';
        risk = 62;
      }
      return {
        id: p.id,
        candidate_id: cand.id,
        candidate_name: cand.name || '—',
        client,
        role: vac?.title || p.data?.jobOrder?.title || '—',
        start_date: formatNlDate(p.start_date),
        stage: (p.status || '—').toString(),
        satisfaction,
        risk_score: risk,
        last_note: p.data?.note || p.data?.lastNote || '',
        next_action: 'WhatsApp · Check-in',
      };
    });
}

export function buildWarmPoolList(poolRows: any[]): MockCandidate[] {
  const out: MockCandidate[] = [];
  for (const row of poolRows) {
    const c = unwrap(row);
    if (!c?.id) continue;
    const stale = daysSince(c.updated_at);
    if (stale === null || stale < 45) continue;
    const { opted_out, consent_ok } = consentFlags(c);
    if (!consent_ok || opted_out) continue;
    let warmth: WarmthStatus = stale >= 120 ? 'sleeping' : stale >= 90 ? 'passive' : 'warm';
    out.push({
      id: c.id,
      name: c.name || '—',
      email: c.email || '',
      phone: c.phone || '',
      role: c.role || '',
      location: c.address_city || '—',
      warmth,
      last_contact: formatNlDate(c.updated_at),
      engagement_score: Math.min(92, 50 + Math.min(35, Math.floor(stale / 4))),
      opted_out,
    });
  }
  out.sort((a, b) => b.engagement_score - a.engagement_score);
  return out.slice(0, 24);
}

export function buildAssignmentEndings(placements: any[]): AssignmentEndingRow[] {
  const rows: AssignmentEndingRow[] = [];
  for (const p of placements || []) {
    if (!p?.end_date || !p.candidate) continue;
    const until = daysUntil(p.end_date);
    if (until === null || until < 0 || until > 90) continue;
    const vac = p.vacancy;
    const client =
      p.opdrachtgever?.name || p.eindklant?.name || vac?.client?.name || '—';
    rows.push({
      id: `end-${p.id}`,
      candidate_id: p.candidate.id,
      candidate_name: p.candidate.name || '—',
      client,
      assignment: vac?.title || '—',
      end_date: formatNlDate(p.end_date),
      end_date_iso: typeof p.end_date === 'string' ? p.end_date : String(p.end_date),
      days_left: Math.max(0, until),
      availability: p.candidate.availability || '—',
      next_match: 'Interne matching — openstaande vacatures',
    });
  }
  rows.sort((a, b) => {
    const ta = a.end_date_iso ? Date.parse(a.end_date_iso) : NaN;
    const tb = b.end_date_iso ? Date.parse(b.end_date_iso) : NaN;
    if (!Number.isNaN(ta) && !Number.isNaN(tb) && ta !== tb) return ta - tb;
    return a.days_left - b.days_left;
  });
  return rows.slice(0, 40);
}

export function buildDataConsentRows(poolRows: any[]): DataConsentRow[] {
  const rows: DataConsentRow[] = [];
  for (const row of poolRows.slice(0, 50)) {
    const c = unwrap(row);
    if (!c?.id) continue;
    const data = c.data || {};
    const cvUp = c.updated_at;
    const gdprOk = consentFlags(c).consent_ok && !consentFlags(c).opted_out;
    rows.push({
      candidate_id: c.id,
      name: c.name || '—',
      cv_updated: formatNlDate(cvUp),
      availability_updated: data.availability_updated || null,
      salary_updated: data.salary_updated || null,
      location_updated: data.location_updated || null,
      consent_date: data.consent_date || null,
      consent_expiry: data.consent_expiry || null,
      gdpr_ok: gdprOk,
      preferred_channel: 'email',
      opted_out: consentFlags(c).opted_out,
    });
  }
  return rows;
}

export function summarizeTasksForKpis(
  tasks: EngagementTask[],
  poolRows: any[],
  placements: any[],
  endingRows: AssignmentEndingRow[]
) {
  const highPri = tasks.filter((t) => t.priority === 'high' || t.priority === 'critical').length;
  let warm = 0;
  for (const row of poolRows) {
    const c = unwrap(row);
    const d = daysSince(c.updated_at);
    if (d !== null && d >= 60) warm++;
  }
  const placementRisk = tasks.filter((t) => t.task_type === 'placement_followup').length;
  return {
    todayCount: tasks.length,
    highMatch: highPri,
    warmFollowup: warm,
    endingSoon: endingRows.length,
    placementRisk,
  };
}

export function taskToBulkCandidate(task: EngagementTask, vacancy?: Vacancy | null): BulkCandidate {
  return {
    id: task.candidate_id,
    profile_id: task.candidate_id,
    name: task.candidate_name,
    email: task.candidate_email || '',
    phone: task.candidate_phone,
    vacancyTitle: vacancy?.title,
    companyName: vacancy ? pickCompany(vacancy) : undefined,
  };
}

/** Minimal task for list contact buttons (vacancy match, warm pool, etc.). */
export function minimalTaskForContact(
  c: { id: string; name: string; email?: string; phone?: string; opted_out?: boolean },
  vacancy: Vacancy | null | undefined,
  user: User,
  opts?: { message?: string; reason?: string }
): EngagementTask {
  const now = new Date().toISOString();
  const first = (c.name || '').split(/\s+/)[0] || c.name || 'daar';
  const company = vacancy ? pickCompany(vacancy) : '';
  const vacTitle = vacancy?.title || '';
  const msg =
    opts?.message ||
    (vacTitle
      ? `Hoi ${first},\n\nIk wil graag met je praten over de rol "${vacTitle}"${company ? ` bij ${company}` : ''}.\n\nGroet,\n${user.name || ''}`
      : `Hoi ${first},\n\nIk wil graag even contact met je opnemen.\n\nGroet,\n${user.name || ''}`);
  return {
    id: `list-contact-${c.id}-${vacancy?.id || 'novac'}`,
    candidate_id: c.id,
    candidate_name: c.name,
    candidate_email: c.email || '',
    candidate_phone: c.phone || '',
    vacancy_id: vacancy?.id || null,
    placement_id: null,
    task_type: 'new_vacancy_outreach',
    reason: opts?.reason || 'Contact vanuit engagement',
    priority: 'medium',
    engagement_score: 55,
    ai_confidence: 0.55,
    suggested_channel: 'whatsapp_personal',
    suggested_send_time: '—',
    generated_message: msg,
    status: 'ready_to_contact',
    assigned_recruiter_id: user.id,
    assigned_recruiter_name: user.name || '',
    last_contacted_at: null,
    created_at: now,
    updated_at: now,
    consent_ok: !c.opted_out,
    opted_out: !!c.opted_out,
    current_status: '—',
  };
}
